import { useEffect, useState } from 'react';
import { Icon } from '@/components/entities/Sprite';
import { Input } from '@/components/ui/Input/input';
import { useUrlSearch } from '@/hooks/useUrlSearch';
import { cn } from '@/utils/cn';

type GamesSearchBarProps = {
  placeholder: string;
  searchKey?: string;
  className?: string;
  delay?: number;
  resetPageOnSearch?: boolean;
  onSearchChange?: (value: string) => void;
};

export function GamesSearchBar({
  delay,
  placeholder,
  className,
  searchKey = 'search',
  resetPageOnSearch,
  onSearchChange,
}: GamesSearchBarProps) {
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const { searchValue, setSearchValue, searchParam } = useUrlSearch({
    delay,
    searchKey,
    resetPageOnSearch,
  });

  useEffect(() => {
    onSearchChange?.(searchParam);
  }, [onSearchChange, searchParam]);

  const handleClear = () => {
    setSearchValue('');
    onSearchChange?.('');
    setIsSearchFocused(false);
  };

  return (
    <div className={cn('absolute right-0 w-full flex justify-end', className)}>
      <Input
        placeholder={placeholder}
        value={searchValue}
        onChange={event => setSearchValue(event.target.value)}
        containerClassName={cn('transition-all duration-300 size-11 md:w-[15.625rem]', isSearchFocused && 'w-full')}
        className={cn(
          'pr-0 transition-all duration-300 ml-auto h-11 w-full lg:pl-12',
          isSearchFocused ? 'pl-12' : 'max-md:placeholder:opacity-0',
        )}
        icon={<Icon name="search" className="text-c300 size-6" />}
        iconWrapperClassName={!isSearchFocused ? 'max-md:left-1/2 max-md:-translate-x-1/2' : undefined}
        onFocus={() => setIsSearchFocused(true)}
      />

      {searchValue && (
        <button
          onClick={handleClear}
          type="button"
          className="absolute right-3 top-1/2 -translate-y-1/2 z-10 text-violet-c400"
          aria-label="Clear search"
        >
          <Icon name="x_close" className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
